import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import ArrowRight from "~icons/ph/arrow-right";
import Share2 from "~icons/ph/share-network";
import { useShell } from "../app/shell";
import { useShareProfile, useSubmitting } from "../data/mutations";
import { peopleOptions } from "../data/queries";
import { useAccount } from "../data/session";
import { t } from "../i18n";
import { ProfileLink } from "../links/profile-link";
import { Modal } from "../ui/components";
import { notify } from "../ui/toast";
import { PersonRow } from "./person-row";

function ShareStep({
  code,
  onReset,
}: {
  code: string | null | undefined;
  onReset: () => void;
}) {
  const share = useShareProfile();
  const submitting = useSubmitting();
  // The link is made the first time it is shown.
  const requested = useRef(false);
  useEffect(() => {
    if (code || requested.current) return;
    requested.current = true;
    share.mutate(false);
  }, [code, share]);
  return (
    <>
      <h2>{t("مشاركة رابط الحساب")}</h2>
      <p className="modal-description">
        {t(
          "أرسل رابط حسابك أو اعرض رمز QR. من يفتحه يمكنه أن يرسل إليك طلب إضافة.",
        )}
      </p>
      <ProfileLink
        code={code}
        failed={share.isError}
        disabled={submitting}
        onRetry={() => share.mutate(false)}
        onReset={onReset}
      />
    </>
  );
}

// Adding a friend starts with their whole username; they can also add this
// account through its profile link or QR code. Sharing opened from here comes
// back with the search still there, and a link reset returns to sharing.
export function AddFriendDialog({ step: start }: { step: "add" | "share" }) {
  const shell = useShell();
  const account = useAccount();
  const accountId = account?.me.id ?? null;
  const reset = useShareProfile();
  const submitting = useSubmitting();
  const [step, setStep] = useState<"add" | "share" | "reset-link">(start);
  // The username typed to add a friend, and the one submitted to search.
  const [lookup, setLookup] = useState(""),
    [query, setQuery] = useState("");
  // A search runs for the whole username that was submitted; submitting
  // another aborts the previous one.
  const search = useQuery({
    ...peopleOptions(accountId, query),
    enabled: !!accountId && !!query,
  });
  const people = search.data ?? [];
  const searching = !!query && search.isPending;
  const searchError = search.error?.message ?? "";
  function findUsername(e: FormEvent) {
    e.preventDefault();
    const username = lookup.trim().replace(/^@/, "").toLowerCase();
    if (username === query) void search.refetch();
    else setQuery(username);
  }
  async function resetProfileLink() {
    try {
      await reset.mutateAsync(true);
      setStep("share");
      notify(t("تم تغيير رابط حسابك. الرابط السابق لم يعد يعمل."), "success");
    } catch (e) {
      notify((e as Error).message);
    }
  }
  const back =
    step === "reset-link"
      ? () => setStep("share")
      : step === "share" && start === "add"
        ? () => setStep("add")
        : null;
  return (
    <Modal
      key={step}
      title={
        step === "add"
          ? t("إضافة صديق")
          : step === "share"
            ? t("مشاركة رابط الحساب")
            : t("إعادة تعيين الرابط")
      }
      onClose={back ?? shell.close}
      closeLabel={back ? t("رجوع") : undefined}
      closeIcon={
        back ? (
          <ArrowRight className="directional" width={19} height={19} />
        ) : undefined
      }
      className={step === "reset-link" ? "" : "list-modal"}
    >
      {step === "add" && (
        <>
          <h2>{t("إضافة صديق")}</h2>
          <form className="username-search" onSubmit={findUsername}>
            <label htmlFor="username-search">{t("اسم مستخدم صديقك")}</label>
            <div className="username-search-field">
              <input
                data-autofocus
                id="username-search"
                value={lookup}
                onChange={(e) => setLookup(e.target.value)}
                placeholder="@username"
                aria-describedby="username-search-help"
                maxLength={25}
                dir="ltr"
                autoCapitalize="none"
                autoComplete="off"
                spellCheck={false}
              />
              <button className="button secondary" disabled={!lookup.trim()}>
                {t("بحث")}
              </button>
            </div>
            <small id="username-search-help" className="field-help">
              {t("اكتب اسم المستخدم كاملًا كما هو.")}
            </small>
          </form>
          <section
            className="search-results"
            aria-label={t("نتائج البحث")}
            aria-live="polite"
          >
            {!query ? null : searching ? (
              <div className="search-skeleton" aria-label={t("جاري البحث")}>
                <div>
                  <i />
                  <span>
                    <b />
                    <b />
                  </span>
                </div>
              </div>
            ) : searchError ? (
              <div className="inline-empty">
                <p>{t(searchError)}</p>
                <button
                  className="quiet-button"
                  onClick={() => void search.refetch()}
                >
                  {t("إعادة المحاولة")}
                </button>
              </div>
            ) : people.length ? (
              people.map((p) => (
                <PersonRow
                  key={p.id}
                  person={p}
                  onOpen={(person) =>
                    shell.open({ kind: "person", person, search: query })
                  }
                />
              ))
            ) : (
              <div className="inline-empty">
                <p>
                  {t("لم نجد أحدًا باسم المستخدم {username}.", {
                    username: `@${query}`,
                  })}
                </p>
                <small>
                  {t(
                    "تأكد من كتابته كاملًا، أو اطلب رابط حسابه. قد لا يظهر بعض الأشخاص في البحث.",
                  )}
                </small>
              </div>
            )}
          </section>
          <section className="add-share" aria-labelledby="add-share-title">
            <h3 id="add-share-title">{t("أو دع صديقك يضيفك")}</h3>
            <p>{t("أرسل إليه رابط حسابك أو اعرض رمز QR.")}</p>
            <button
              className="button secondary"
              onClick={() => setStep("share")}
            >
              <Share2 width={15} height={15} aria-hidden="true" />
              {t("مشاركة رابط الحساب")}
            </button>
          </section>
        </>
      )}
      {step === "share" && (
        <ShareStep
          code={account?.me.link_code}
          onReset={() => setStep("reset-link")}
        />
      )}
      {step === "reset-link" && (
        <>
          <h2>{t("إعادة تعيين رابط حسابك؟")}</h2>
          <p className="modal-description">
            {t(
              "سيتوقف رابطك الحالي ورمز QR عن العمل، وتحصل على رابط جديد. من أصبحوا أصدقاءك يبقون كذلك.",
            )}
          </p>
          <div className="confirm-actions">
            <button
              className="button secondary"
              disabled={submitting}
              onClick={() => setStep("share")}
            >
              {t("رجوع")}
            </button>
            <button
              className="button"
              disabled={submitting}
              onClick={() => void resetProfileLink()}
            >
              {t("إعادة تعيين الرابط")}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
